import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { YourContactVariable } from 'enums/LoanFormVariables';
import { ConversionType, trackConversion } from 'utils/analytics';
import { hstreamCallback } from 'thunks';
import { HStreamCallbackResponse } from 'api/AuthApi';

export interface ContactResendState {
  lastSentAt: number;
  attemptCount: number;
}

export enum ContactType {
  Email = 'email',
  Phone = 'phone',
}

export interface YourContactData {
  [YourContactVariable.Email]: string;
  [YourContactVariable.PhoneNumber]: string;
  emailResendState: ContactResendState | null;
  phoneResendState: ContactResendState | null;
}

export const initialState: YourContactData = {
  [YourContactVariable.Email]: '',
  [YourContactVariable.PhoneNumber]: '',
  emailResendState: null,
  phoneResendState: null,
};

const yourContact = createSlice({
  name: 'yourContact',
  initialState,
  reducers: {
    setYourContactData: (state: YourContactData, { payload }: PayloadAction<Partial<YourContactData>>) => {
      const email = payload[YourContactVariable.Email];
      if (email) {
        analytics.identify({ email });
        if (!state[YourContactVariable.Email]) {
          trackConversion(ConversionType.CollectedEmail);
        }

        state[YourContactVariable.Email] = email;
      }

      const phoneNumber = payload[YourContactVariable.PhoneNumber];
      if (phoneNumber) {
        analytics.identify({ phoneNumber });
        if (!state[YourContactVariable.PhoneNumber]) {
          trackConversion(ConversionType.CollectedPhone);
        }

        state[YourContactVariable.PhoneNumber] = phoneNumber;
      }
    },
    setResendState: (
      state: YourContactData,
      { payload }: PayloadAction<{ contactType: ContactType; state: ContactResendState | null }>,
    ) => {
      if (payload.contactType === ContactType.Email) {
        state.emailResendState = payload.state;
      } else {
        state.phoneResendState = payload.state;
      }
    },
  },
  extraReducers: (builder) => {
    builder.addCase(hstreamCallback.fulfilled, (state, { payload }: PayloadAction<HStreamCallbackResponse>) => {
      const { userInfo } = payload;
      state[YourContactVariable.Email] = userInfo.email;
    });
  },
});

export const { setYourContactData, setResendState } = yourContact.actions;

export default yourContact.reducer;
